import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslateService {
  private translations: Record<string, string> = {};
  readonly currentLang$ = new BehaviorSubject<string>('it');

  // Ogni lingua in caricamento tiene la sua Promise — i caller concorrenti
  // sulla stessa lingua aspettano la stessa Promise invece di ricevere un
  // ritorno falso "già fatto".
  private readonly pendingLoads = new Map<string, Promise<void>>();

  // Timestamp fisso per la sessione: stessa URL nell'arco della sessione
  // (quindi cache hit), URL diversa ad ogni refresh (niente JSON stantio).
  private static readonly CB = Date.now();

  constructor(private readonly http: HttpClient) {}

  get currentLang(): string { return this.currentLang$.getValue(); }

  async load(lang: string): Promise<void> {
    const pending = this.pendingLoads.get(lang);
    if (pending) return pending;

    const promise = this._fetch(lang);
    this.pendingLoads.set(lang, promise);
    try {
      await promise;
    } finally {
      this.pendingLoads.delete(lang);
    }
  }

  private async _fetch(lang: string): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.http.get<Record<string, string>>(`/assets/i18n/${lang}.json?v=${TranslateService.CB}`)
      );
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error(`Invalid translation file for: ${lang}`);
      }
      this.translations = data;
      this.currentLang$.next(lang);
      localStorage.setItem('preferredLang', lang);
    } catch (error) {
      console.error(`Failed to load translations for language: ${lang}`, error);
      // Fallback su italiano se non era già italiano e le traduzioni sono vuote
      if (lang !== 'it' && Object.keys(this.translations).length === 0) {
        try {
          const fallback = await firstValueFrom(
            this.http.get<Record<string, string>>(`/assets/i18n/it.json?v=${TranslateService.CB}`)
          );
          if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) {
            this.translations = fallback;
            this.currentLang$.next('it');
          }
        } catch (fallbackError) {
          console.error('Failed to load fallback translations (it)', fallbackError);
        }
      }
    }
  }

  translate(key: string): string {
    if (Object.keys(this.translations).length === 0) return key;
    return key.split('.').reduce((obj: any, part: string) => obj?.[part], this.translations) ?? key;
  }
}
